import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Transition } from "@headlessui/react";
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import Dropdown from "../../../shared/components/Dropdown";
import ToggleButton from "../../../shared/components/ToggleButton";
import useClickOutside from '../../../shared/hooks/useClickOutside';
import { useStatsData } from "../../../mls-player-stats/utils";
import { actionTypes, useFilters } from "../../hooks/useFilters";
import { filterTypes, playerStatsPositionTypes } from "../../utils";
import { tableTypes } from "../Table/utils";
import { useReactAppState } from "../../../shared/containers/utils";

const StatsSidebar = (props) => {
  const { t } = useReactAppState();

  const {
    setIsOpen,
    isOpen,
    querySelector = 'body',
    title,
    tableType = tableTypes.players,
    initialState,
    enableAllYears = false
  } = props || {};

  const {
    updateFilter,
    resetFilters,
    clubSportecId: currentClubSportecId,
    competitionSportecId: currentCompetitionSportecId,
    season: currentSeason,
    position: currentPosition,
    appearances: currentAppearances,
    weather: currentWeather,
    versus: currentVersus,
    dateRange: currentDateRange,
    competitionSlug: currentCompetitionSlug,
    mlsPhaseType: currentMlsPhaseType,
    clubs,
    seasons,
    competitions,
    preSelectedClub,
  } = useStatsData();

  const { state, reset, updateFilter: updateSidebarFilters } = useFilters({
    initialState: {
      competitions,
      season: currentSeason,
      competitionSlug: currentCompetitionSlug,
      competitionSportecId: currentCompetitionSportecId,
      clubSportecId: currentClubSportecId,
      position: currentPosition,
      appearances: currentAppearances,
      versus: currentVersus,
      weather: currentWeather,
      dateRange: currentDateRange,
      mlsPhaseType: currentMlsPhaseType,
    }
  });

  const { mlsPhaseType, season, clubSportecId, position, appearances, weather, competitionSlug } = state || {};

  const node = useRef();
  const nodeSelect = useRef();
  const [isDisabled, setIsDisabled] = useState(true);

  useClickOutside(node, setIsOpen);

  useEffect(() => {
    updateSidebarFilters(actionTypes.apply)({
      versus: currentVersus,
      clubSportecId: currentClubSportecId,
      position: currentPosition,
      competitionSportecId: currentCompetitionSportecId,
      competitionSlug: currentCompetitionSlug,
      dateRange: currentDateRange,
      appearances: currentAppearances,
      weather: currentWeather,
      season: currentSeason,
      mlsPhaseType: currentMlsPhaseType,
    });
  }, [
    currentCompetitionSportecId,
    currentSeason,
    currentCompetitionSlug,
    currentClubSportecId,
    currentDateRange,
    currentVersus,
    currentPosition,
    currentAppearances,
    currentWeather,
    currentMlsPhaseType,
  ]);

  useEffect(() => {
    if (
      season !== currentSeason ||
      weather !== currentWeather ||
      competitionSlug !== currentCompetitionSlug ||
      clubSportecId !== currentClubSportecId ||
      position !== currentPosition ||
      appearances !== currentAppearances) {
      setIsDisabled(false);
    }
    else {
      setIsDisabled(true);
    }
  }, [season, competitionSlug, clubSportecId, position, appearances, weather, currentSeason, currentWeather, currentCompetitionSlug, currentClubSportecId, currentPosition, currentAppearances]);

  useLayoutEffect(() => {
    if (isOpen) {
      disableBodyScroll(node.current);
      setTimeout(() => {
        nodeSelect?.current?.querySelector("select")?.focus();
      }, 750)
    }
    else {
      clearAllBodyScrollLocks();
    }
  }, [isOpen]);

  const clearState = React.useCallback((e) => {
    e.preventDefault();

    const { competitionSlug, season, mlsPhaseType } = initialState || {};

    const payload = {
      season,
      competitionSlug,
      mlsPhaseType,
      clubSportecId: filterTypes.allClubs,
      position: filterTypes.all,
      appearances: filterTypes.all,
      versus: filterTypes.all,
      weather: filterTypes.all,
      dateRange: filterTypes.all,
    };

    if (preSelectedClub) {
      const { clubSportecId, ...rest } = payload;
      reset(rest);
      resetFilters(rest);
    }

    else {
      reset(payload);
      resetFilters(payload);
    }
  }, [initialState, preSelectedClub]);

  const applyFilters = React.useCallback((e) => {
    e.preventDefault();

    setIsOpen(false);
    updateFilter(actionTypes.apply)(state);
  }, [setIsOpen, updateFilter, state]);

  const mappedSeasons = seasons?.seasons?.map((s) => {
    return { label: s.season, value: s.season };
  });

  return ReactDOM.createPortal(
    <div className="mls-o-overlay">
      <div className="mls-o-overlay__wrapper">
        <Transition show={isOpen}
          className="mls-o-overlay__overlay"
          enter="ease-in-out duration--200"
          enterFrom="opacity--0"
          enterTo="opacity--100"
          leave="ease-in-out duration--200"
          leaveFrom="opacity--100"
          leaveTo="opacity--0">
          <div />
        </Transition>

        <section ref={node} className={`mls-o-side-bar`}>
          <Transition
            show={isOpen}
            as="aside"
            enterFrom="translate-x--full"
            enter="transform transition ease-in-out duration--200"
            enterTo="translate-x--0"
            leaveFrom="translate-x--0"
            leave="transform transition ease-in-out duration--200"
            leaveTo="translate-x--full"
            className={`mls-o-side-bar__aside`}>
            {
              title
                ? <div className={"mls-o-side-bar__title"}>{title}</div>
                : null
            }
            <ul
              ref={nodeSelect}
              body-scroll-lock-ignore={"true"}
              className="mls-o-side-bar__list">
              <Dropdown
                autoWidth={false}
                onChangeFn={updateSidebarFilters(actionTypes.season)}
                items={
                  seasons ? (enableAllYears ?
                    [{ label: t("all_years"), value: "all" },
                    ...mappedSeasons]
                    : mappedSeasons) : []
                }
                displayKey={"label"}
                valueKey={"value"}
                currentValue={season}
              />
              <Dropdown
                autoWidth={false}
                valueKey={"slug"}
                displayKey={"name"}
                onChangeFn={updateSidebarFilters(actionTypes.competitionSlug)}
                items={competitions}
                currentValue={competitionSlug}
              />
              {
                tableType !== tableTypes.player
                  ? (
                    <>
                      {
                        !preSelectedClub ? (
                          <Dropdown
                            autoWidth={false}
                            onChangeFn={updateSidebarFilters(actionTypes.clubSportecId)}
                            displayKey={"team_short_name"}
                            valueKey={"team_id"}
                            items={clubs}
                            currentValue={clubSportecId} />
                        ) : null
                      }
                      <Dropdown
                        autoWidth={false}
                        onChangeFn={updateSidebarFilters(actionTypes.position)}
                        items={Object.values(playerStatsPositionTypes)}
                        currentValue={position} />
                    </>
                  )
                  : null
              }
              {/*<Dropdown label={"appearances"}*/}
              {/*          autoWidth={false}*/}
              {/*          onChangeFn={updateSidebarFilters(actionTypes.appearances)}*/}
              {/*          items={["All", ...Array.from(Array(18).keys())]}*/}
              {/*          currentValue={appearances}/>*/}
              {/*<Dropdown label={"weather"}*/}
              {/*          autoWidth={false}*/}
              {/*          onChangeFn={updateSidebarFilters(actionTypes.weather)}*/}
              {/*          items={["All", "Sunny", "Windy", "Rainy"]}*/}
              {/*          currentValue={weather}/>*/}
            </ul>
            <div className="mls-o-side-bar__footer">
              <ToggleButton className="mls-o-buttons__basic mls-o-buttons__basic--no-outline" onClick={(e) => clearState(e)}>{t("clear_filters")}</ToggleButton>
              <ToggleButton disabled={isDisabled} className="mls-o-buttons__basic mls-o-buttons__basic--filled" onClick={(e) => applyFilters(e)}>{t("apply_filters")}</ToggleButton>
            </div>
          </Transition>
        </section>
      </div>
    </div>,
    document.querySelector(querySelector)
  );
};

export default StatsSidebar;
